<template>
  <header
    class="px-8 lg:px-20 sm:px-16 relative"
    :class="{'light-theme': whiteLogo}"
  >
    <div
      class="flex justify-between pt-5 lg:pb-0 pb-5 relative z-50"
    >
      <!-- Logo -->
      <div class="nv-d-flex">
        <div class="nv-d-flex">
          <nuxt-link id="header-logo" :to="`/${$i18n.locale}`" class="nv-d-flex">
            <img
              :src="`https://assets.nuinalp.com/logo/logo-typo${whiteLogo ? '-white' : ''}.svg`"
              alt="Nuinalp logo"
              class="logo-typo"
            >
          </nuxt-link>
        </div>
        <Menu
          v-if="displayMenu"
          :menu-items="defaultMenuItems"
          :section-menu-items="sectionMenuItems"
          :sub-menu-itens="defaultSubMenuItens"
        />
      </div>
      <!-- Right -->
      <div v-if="displayMenu" class="nv-d-flex nv-align-items-center">
        <a
          v-if="$config.public.ENABLE_ACCOUNTS === 'true'"
          :href="nuinalpAccountsPath"
        >
          <nv-button circle text>
            <nv-icon>person</nv-icon>
          </nv-button>
        </a>
        <ClientOnly>
          <portal-target v-if="displayMenu" name="menu-mobile-btn" />
        </ClientOnly>
      </div>
    </div>
    <ClientOnly>
      <portal-target v-if="displayMenu" name="menu-bg" />
      <portal-target v-if="displayMenu" name="menu-items-container" />
    </ClientOnly>

    <!-- Title page section -->
    <div class="empty:hidden">
      <ClientOnly>
        <portal-target class="nv-py-8 header-title" name="header-title" />
      </ClientOnly>
    </div>

    <!-- Product Menu -->
    <div ref="productSubMenu" class="w-full flex justify-between items-center sticky z-10 top-0 lg:py-4">
      <SubMenu
        class="flex lg:hidden"
        v-if="subMenuItens.length"
        :product-name="productName"
        :subMenuItems="subMenuItens"
      />

      <span class="font-bold text-2xl product-name pr-3 mr-3 hidden lg:block">
        {{ productName }}
      </span>
      
      <!-- Sub menu items -->
      <ul class="hidden lg:flex gap-4 flex-1 flex-nowrap items-center">
        <li
          v-for="(subMnItem, idx) in subMenuItens"
          :key="idx"
        >
          <a :href="subMnItem.anchor || subMnItem.url || '#'">
          {{ subMnItem.title }}
          </a>
        </li>
      </ul>
    </div>
  </header>
</template>

<script lang="ts" setup>
// Imports
// Utils
import routesBuild from '@/utils/routes.build';
import subMenuRoutes from '@/utils/menu-subitens';
import type { PropType } from 'vue';

// ---------------------------------------------
// Hooks
// ---------------------------------------------
const { t, locale } = useI18n();
const { $currentCallbackUrl, $config } = useNuxtApp();

defineProps({
  productName: { default: undefined, type: String, required: false },
  menuItems: { type: Array, required: false, default: undefined },
  sectionMenuItems: { type: Array, required: false, default: undefined },
  sectionSubMenuItens: { type: Array, required: false, default: undefined },
  subMenuItens: { type: Array as PropType<IRoutesLinksResponse[]>, required: false, default: undefined },
  displayMenu: { default: true, type: Boolean, required: false },
  whiteLogo: { default: false, type: Boolean, required: false },
  displaySubMenu: { default: true, type: Boolean, required: false },
})

// ---------------------------------------------
// Computed
// ---------------------------------------------
const productSubMenu = ref<HTMLDivElement>();
const defaultMenuItems = computed(() => routesBuild(locale.value, [
  { title: t('home_products'), id: 'home_products' },
  { title: t('business_products'), id: 'business_products', enabled: false },
  { title: t('company'), id: 'company' },
  {
    title: t('support'), external: true, url: $config.public.NNP_SUPPORT_URL, enabled: true,
  },
  {
    title: t('blog'), url: '/organizations', id: 'blog', enabled: false,
  },
]));
const initialOffSet = ref<undefined | number>(undefined);

const nuinalpAccountsPath = computed(() => `${$config.public.NNP_ACCOUNTS_URL}/sign-in?lang=${locale.value}&callback=${$currentCallbackUrl()}`);

const defaultSubMenuItens = computed(() => subMenuRoutes(
  locale.value,
  (v: string) => t(v),
));

onMounted(() => {
  if (initialOffSet.value === undefined) {
    initialOffSet.value = productSubMenu.value?.offsetTop || 0;
  }

  window.addEventListener('scroll', function() {
    const stickyOffset = initialOffSet.value;

    if (productSubMenu.value && stickyOffset !== undefined) {
      if (window.pageYOffset > stickyOffset) {
        return productSubMenu.value.classList.add('sticky-sub-menu');
      }

      productSubMenu.value.classList.remove('sticky-sub-menu');
      
    }
  });
})

</script>

<style lang="scss" scoped>
.logo-typo {
  width: 200px;
}

header {
  z-index: 110;
}

.header-content {
  position: relative;
  z-index: 106;
}

.product-name {
  position: relative;

  &::before {
    background-color: var(--nv-type-primary);
    content: '';
    height: 80%;
    right: 0;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 2px;
  }
}

.nv-grid {
  position: relative;
  z-index: 1000;
}

.light-theme {
  color: #fff;

  nv-icon,
  .menu-mobile-icon nv-icon {
    color: #fff;
  }

  ::before,
  ::after {
    background-color: #fff !important;
  }

  &::before {
    background-image: linear-gradient(#48c7a5ff, #0fa6dc);
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    z-index: -1;
  }

  a {
    color: #fff;
  }
}

.header-title:empty {
  display: none;
}

.sticky-sub-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.9);
  @apply px-8;
  border-bottom: 1px solid var(--nv-border-color);

  nav {
    border-top: none;
  } 
}


@media (screen(sm)) {
  .sticky-sub-menu {
    @apply px-16;
  }
}

@media (screen(lg)) {
  .sticky-sub-menu {
    @apply px-20;
  }
}
</style>
